import { DateTime } from 'luxon'
import { NextSeo } from 'next-seo'
import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import {
  GoToTop,
  Retry,
  SharedOrderEmptyList,
  SharedOrderList,
  styled,
  useGoToTop,
} from '~/app/components'
import { isTypeOf, notEmpty } from '~/app/utils'
import {
  SharedOrderQuery,
  useSharedOrderLazyQuery,
} from '~/__generated__/app/query/order-shared/order-shared-list.graphql'
import {
  CartLeisureOrderStatus,
  CartTrainOrderTicketStatus,
  StatusGroupCode,
} from '~/__generated__/__types__'
import { getFilteredShareBusOrder } from '~/app/components/order-converter/converter/BusOrderConverter'
import { useServerCurrentAtQuery } from '~/__generated__/app/query/server-current-at.graphql'
import { getYaNative } from '@yanolja-org/ya-native'
import conf from '~/app/conf'
import { getNowSeoulDateTime } from '~/app/utils/datetime'
import { isNative } from '~/app/utils/typeguards'
import { getStatus } from '~/app/utils/useErrorHandler'
import { Typography } from '@yanolja-org/ya-com-core'
import { Layout, ContentContainer, Content } from '~/app/components/layout-style'
import { getAllCategoryOrders } from '~/app/components/order-share/utils'

/**
 * SharePage component
 * author: soojin.oh
 */

// type definitions
type Props = InferGetServerSidePropsType<typeof getServerSideProps>

const SharePageSeo = {
  title: 'NOL예약',
  openGraph: {
    title: 'NOL예약',
    images: [{ url: 'https://yaimg.yanolja.com/joy/sunny/static/images/nol/nol_og_800x800.png' }],
  },
}

// styled components
const ContentWrap = styled(Content)`
  background: white;
  justify-content: flex-start;
  padding: 0 1rem;
  min-height: 100vh;
`
const ReservationNumber = styled.div`
  margin: 1rem;
  padding: 1.4rem 1.6rem;
  border-radius: 0.4rem;
  background: ${({ theme }) => theme.color.gray100};
  ${Typography('body2').regular};
  text-align: center;
`

type SharedOrder = SharedOrderQuery['sharedOrder']
type CategoryOrder = SharedOrder['categoryOrders'][number]
type AccommodationOrder = Extract<CategoryOrder, { __typename?: 'SharedOrderDomestic' }>
type LeisureOrder = Extract<CategoryOrder, { __typename?: 'SharedOrderLeisure' }>
type TrainOrder = Extract<CategoryOrder, { __typename?: 'SharedOrderTrain' }>
type BusOrder = Extract<CategoryOrder, { __typename?: 'SharedOrderBus' }>

const isAccommodationOrder = isTypeOf<AccommodationOrder>('SharedOrderDomestic')
const isLeisureOrder = isTypeOf<LeisureOrder>('SharedOrderLeisure')
const isTrainOrder = isTypeOf<TrainOrder>('SharedOrderTrain')
const isBusOrder = isTypeOf<BusOrder>('SharedOrderBus')

// index component
const SharedOrderPage: FC<Props> = function ({ uuid }) {
  const { isFabOpen, onClick } = useGoToTop()

  const { data: { serverCurrentAt: currentAt } = { serverCurrentAt: getNowSeoulDateTime() } } =
    useServerCurrentAtQuery()
  const [getSharedOrder, { data, error, loading }] = useSharedOrderLazyQuery({
    variables: { uuid },
  })
  const { id } = data?.sharedOrder || {}
  const allCategoryOrders = getAllCategoryOrders(data?.sharedOrder)

  /**
   * 유효한 숙소 주문
   */
  const getAccommodationOrder = useCallback(
    (order: AccommodationOrder) => {
      if (order.statusGroupCode !== StatusGroupCode.Cancel) {
        const isExpired =
          DateTime.fromISO(order.checkOutAt).diff(DateTime.fromISO(currentAt)).toMillis() < 0
        return isExpired ? null : order
      }
    },
    [currentAt]
  )
  /**
   * 유효한 레저 주문
   */
  const getLeisureOrder = useCallback((order: LeisureOrder) => {
    /**
     * 레저 상품 미노출 조건
     * - 사용되지 않은 아이템
     * - 유효기간 종료일 이후
     */
    const items = order.items
      .filter(item => item.status === CartLeisureOrderStatus.Unused)
      .filter(item => {
        if (!item.variant.validityEndDateTime) {
          return false
        }

        const isExpired =
          DateTime.fromFormat(item.variant.validityEndDateTime, 'yyyy-MM-dd HH:mm')
            .diff(DateTime.fromISO(currentAt))
            .toMillis() < 0

        return !isExpired
      })

    if (items.length === 0) {
      return null
    }
    return {
      ...order,
      items,
    }
  }, [])
  /**
   * 유효한 기차 주문
   */
  const getTrainOrder = useCallback(
    (order: TrainOrder) => {
      const tickets = order.tickets.filter(ticket => {
        if (ticket.status === CartTrainOrderTicketStatus.Reserved) {
          const isExpired =
            DateTime.fromISO(ticket.arrivalAt).diff(DateTime.fromISO(currentAt)).toMillis() < 0
          return !isExpired
        }
        return false
      })
      if (tickets.length === 0) {
        return null
      }
      return {
        ...order,
        tickets,
      }
    },
    [currentAt]
  )
  /**
   * 유효한 버스 주문 ( reservations > tickets 확인 )
   */
  const getBusOrder = useCallback(
    (order: BusOrder) => {
      const { reservations, reservationTicketCount } = getFilteredShareBusOrder(
        order.reservations,
        currentAt
      )
      if (reservationTicketCount === 0) {
        return null
      }
      return {
        ...order,
        reservations,
      }
    },
    [currentAt]
  )

  /**
   * 유효한 주문만 찾아서 노출한다
   */
  const availableOrders = useMemo(() => {
    return allCategoryOrders
      .map(order => {
        if (isAccommodationOrder(order)) {
          return getAccommodationOrder(order)
        } else if (isLeisureOrder(order)) {
          return getLeisureOrder(order)
        } else if (isTrainOrder(order)) {
          return getTrainOrder(order)
        } else if (isBusOrder(order)) {
          return getBusOrder(order)
        }
      })
      .filter(notEmpty)
  }, [allCategoryOrders])
  const isEmpty = useMemo(() => (availableOrders?.length || 0) < 1, [availableOrders])
  const onRefresh = useCallback(() => {
    if (uuid) {
      getSharedOrder({
        variables: { uuid },
      })
    }
  }, [uuid])
  useEffect(() => {
    if (uuid) {
      getSharedOrder({ variables: { uuid } })
    }
  }, [uuid])

  const notFound = useMemo(() => {
    const { graphQLErrors } = error || {}
    return (graphQLErrors?.filter(e => getStatus(e) === 404).length || 0) > 0
  }, [error])

  const onBackClick = useCallback(() => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.deepLink({ link: conf.app.home.url })
    } else {
      window.location.replace(conf.web.url)
    }
  }, [])

  return (
    <>
      <NextSeo {...SharePageSeo} />
      <Layout>
        <ContentContainer>
          <ContentWrap>
            {/* notFound 는 EmptyList 를 보여줘야 하기 때문에 예외처리 한다.*/}
            <Retry
              onRetryClick={onRefresh}
              hasError={!notFound && !!error}
              onBackClick={onBackClick}
            >
              <ReservationNumber>통합 주문번호 {id}</ReservationNumber>
              {!loading && (
                <SharedOrderEmptyList isEmpty={isEmpty} notFound={notFound}>
                  <SharedOrderList categoryOrders={availableOrders} />
                </SharedOrderEmptyList>
              )}
            </Retry>
          </ContentWrap>
        </ContentContainer>
        <GoToTop isOpened={isFabOpen} onClick={onClick} />
      </Layout>
    </>
  )
}

export default SharedOrderPage
export const getServerSideProps: GetServerSideProps = async context => {
  const uuid = context.query.uuid
  return {
    props: {
      uuid,
    },
  }
}
